import { Component, computed, effect, inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { Product, Vat, ProductBundle, Tag, File, Supplier } from 'depoto-core/src/entities'
import { CoreService, NotificationService } from '../../../services'
import { GeoConfig, GeoListItemType } from '../../../configs'
import type { EditorConfig } from 'ckeditor5'
import { SchemaUtil } from '../../../utils'
import { ProductDetailService } from '../../../product-detail.service'
import { TranslateService } from '@ngx-translate/core'
import { TagService } from '../../../services/entity-services/tag.service'
import { injectQuery } from '@tanstack/angular-query-experimental'

type Ckeditor5Module = typeof import('ckeditor5')
type CkeditorEditorConstructor = Ckeditor5Module['ClassicEditor']

@Component({
  selector: 'product-form',
  templateUrl: './product-form.component.html',
  styleUrls: ['product-form.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProductFormComponent implements OnInit {
  private tagService = inject(TagService)
  private ckeditorLoadPromise?: Promise<void>
  countries: GeoListItemType[] = []
  ckeditor: CkeditorEditorConstructor | null = null
  ckeditorConfig: EditorConfig | null = null
  ckeditorLoading = true
  ckeditorLoadError = false
  isEnabledModule = false

  readonly queryTags = injectQuery(() => ({
    queryKey: ['tags', 'options'],
    queryFn: async () => {
      return await this.tagService
        .getTags({ sort: 'name', direction: 'asc' }, { id: null, name: null, type: null })
        .then(result => result.items)
    },
    select: data => (data as any[]).filter(tag => tag.type === 'product'),
  }))

  selectedTags = computed(() => {
    return this.productDetailService.product?.tags ? this.productDetailService.product.tags : []
  })

  get productPerex(): string {
    return this.productDetailService.product?.perex ?? ''
  }

  set productPerex(value: string) {
    if (this.productDetailService.product) {
      this.productDetailService.product.perex = value ?? ''
    }
  }

  get productDescription(): string {
    return this.productDetailService.product?.description ?? ''
  }

  set productDescription(value: string) {
    if (this.productDetailService.product) {
      this.productDetailService.product.description = value ?? ''
    }
  }

  // todo dat bacha u tagu kde filtruju
  constructor(
    private core: CoreService,
    public productDetailService: ProductDetailService,
    private notificationService: NotificationService,
    public translateService: TranslateService
  ) {
    effect(() => {
      this.queryTags.data()
    })
    this.isEnabledModule = this.core.services.user.user.company.moduleRelations?.some(
      moduleRelation => moduleRelation.module.id === 'serial-numbers'
    )
    this.countries = GeoConfig.getCountries(this.translateService.currentLang)
    this.translateService.onLangChange.subscribe(() => {
      this.countries = GeoConfig.getCountries(this.translateService.currentLang)
    })
  }

  ngOnInit(): void {
    void this.loadCkeditor()
  }

  private loadCkeditor(): Promise<void> {
    if (this.ckeditorLoadPromise) {
      return this.ckeditorLoadPromise
    }

    this.ckeditorLoading = true
    this.ckeditorLoadError = false
    this.ckeditorLoadPromise = import('ckeditor5')
      .then(ckeditor5 => {
        this.ckeditor = ckeditor5.ClassicEditor
        this.ckeditorConfig = this.createCkeditorConfig(ckeditor5)
      })
      .catch(error => {
        console.warn('Failed to load CKEditor', error)
        this.ckeditorLoadError = true
      })
      .finally(() => {
        this.ckeditorLoading = false
      })

    return this.ckeditorLoadPromise
  }

  private createCkeditorConfig(ckeditor5: Ckeditor5Module): EditorConfig {
    const {
      Autoformat,
      BlockQuote,
      Bold,
      CKFinderUploadAdapter,
      Essentials,
      Heading,
      Image,
      ImageCaption,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      Indent,
      Italic,
      Link,
      List,
      MediaEmbed,
      Paragraph,
      PasteFromOffice,
      PictureEditing,
      Table,
      TableToolbar,
      TextTransformation,
    } = ckeditor5

    return {
      licenseKey: 'GPL',
      plugins: [
        Autoformat,
        BlockQuote,
        Bold,
        CKFinderUploadAdapter,
        Essentials,
        Heading,
        Image,
        ImageCaption,
        ImageStyle,
        ImageTextAlternative,
        ImageToolbar,
        ImageUpload,
        Indent,
        Italic,
        Link,
        List,
        MediaEmbed,
        Paragraph,
        PasteFromOffice,
        PictureEditing,
        Table,
        TableToolbar,
        TextTransformation,
      ],
      toolbar: {
        items: [
          'undo',
          'redo',
          '|',
          'heading',
          '|',
          'bold',
          'italic',
          '|',
          'link',
          'uploadImage',
          'insertTable',
          'blockQuote',
          'mediaEmbed',
          '|',
          'bulletedList',
          'numberedList',
          'outdent',
          'indent',
        ],
      },
      image: {
        toolbar: [
          'imageStyle:inline',
          'imageStyle:block',
          'imageStyle:side',
          '|',
          'toggleImageCaption',
          'imageTextAlternative',
        ],
      },
      table: {
        contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
      },
    }
  }

  public async fillNextEan() {
    this.productDetailService.product.ean = await this.core.services.product.getNextEan()
  }

  public async fillNextCode() {
    this.productDetailService.product.code = await this.core.services.product.getNextCode()
  }

  get isBundleCanBeSet() {
    const product = this.productDetailService.product

    return !(
      (product.isBundle && product.bundleChildren?.length) ||
      product.productDepots?.length ||
      product.children?.length
    )
  }

  setSupplier(event) {
    this.productDetailService.product.supplier = new Supplier({ id: event })
  }

  public async updateVariants(properties: string[]) {
    this.setLoading(true)
    try {
      const productPart: any = {}
      properties.forEach(p => {
        if (p === 'supplier') {
          productPart.supplier = this.productDetailService.product.supplier
        } else if (p === 'purchaseCurrency') {
          productPart.purchaseCurrency = this.productDetailService.product.purchaseCurrency
        } else {
          productPart[p] = this.productDetailService.product[p]
        }
      })
      await this.core.services.product.updateProductPart(
        { id: this.productDetailService.product.id, ...productPart },
        { id: null }
      )
      const promises = this.productDetailService.product.children.map(c =>
        this.core.services.product.updateProductPart({ id: c.id, ...productPart }, { id: null })
      )
      await Promise.all(promises)
      if (promises) {
        this.notificationService.success(this.translateService.instant('alert.product.saved'))
      }
    } catch (e) {
      console.warn(e)
      this.notificationService.warning(this.translateService.instant('product-form.error', { error: e.message }))
    }
    this.setLoading(false)
  }

  public setParent(parent: Product): void {
    this.productDetailService.product.parent = parent
  }

  public addChild(child: Product): void {
    this.productDetailService.product.children.push(child)
  }

  public removeChild(child: Product): void {
    this.productDetailService.product.children.forEach((c, i) => {
      if (child.id === c.id) {
        this.productDetailService.product.children.splice(i, 1)
      }
    })
  }

  async addBundleChild(productBundle: ProductBundle) {
    const existing = this.productDetailService.product.bundleChildren.find(
      bc => bc.child?.id === productBundle.child?.id
    )
    if (existing) {
      await this.updateBundleChild(
        new ProductBundle({ ...existing, quantity: Number(existing.quantity) + Number(productBundle.quantity) })
      )
      return
    }
    if (this.productDetailService.product?.id > 0) {
      this.setLoading(true)
      productBundle.parent = new Product(this.productDetailService.product)
      const result = await this.core.services.productBundle.create(productBundle, SchemaUtil.productBundle)
      if (result && result.id > 0) {
        this.productDetailService.product.bundleChildren.push(result)
      }
      this.setLoading(false)
    } else {
      this.productDetailService.product.bundleChildren.push(productBundle)
    }
  }

  async updateBundleChild(productBundle: ProductBundle) {
    if (this.productDetailService.product?.id > 0) {
      this.setLoading(true)
      const result = await this.core.services.productBundle.update(productBundle, SchemaUtil.productBundle)
      if (result && result.id > 0) {
        this.productDetailService.product.bundleChildren.forEach((bundleChildren, index) => {
          if (result.id === bundleChildren.id) {
            this.productDetailService.product.bundleChildren[index] = result
          }
        })
      }
      this.setLoading(false)
    } else {
      this.productDetailService.product.bundleChildren.forEach((bundleChildren, index) => {
        if (productBundle.child.id === bundleChildren.child.id) {
          this.productDetailService.product.bundleChildren[index] = productBundle
        }
      })
    }
  }

  async removeBundleChild(productBundle: ProductBundle) {
    if (this.productDetailService.product?.id > 0) {
      this.setLoading(true)
      this.productDetailService.product.bundleChildren = [...this.productDetailService.product.bundleChildren].filter(
        bc => bc.id !== productBundle.id
      )
      await this.core.services.productBundle.delete(productBundle)
      this.setLoading(false)
    } else {
      this.productDetailService.product.bundleChildren = [...this.productDetailService.product.bundleChildren].filter(
        bc => bc.child.id !== productBundle.child.id
      )
    }
  }

  public compareWithFn(a: Vat, b: Vat) {
    return a && b ? a.id === b.id : a === b
  }

  onTagsChange(event) {
    this.productDetailService.product.tags = this.queryTags.data().filter(t => event.includes(t.id))
  }

  setLoading(val: boolean) {
    this.productDetailService.loading = val
  }

  getThumbUrl(image: File): string {
    return image.thumbnails?.length > 0 ? image.thumbnails.filter(t => t.format === 'w135wp')[0].url : ''
  }

  get vat() {
    if (this.productDetailService.product?.vat?.id) {
      return this.productDetailService.product?.vat?.id
    } else {
      this.productDetailService.product.vat = this.productDetailService.queryVats.data().filter(v => v.default)[0]
      return this.productDetailService.product.vat.id
    }
  }
}
